import { RISK_COLORS } from "@app/theme/tokens";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import type { PidTag, SpofKks } from "../types";
import { days, mw } from "../utils/format";
import { RiskChip } from "./Chips";
import { PendingValue } from "./PendingValue";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
}

export default function SelectedEquipmentPanel({
  tag,
  onClose,
  onSelectKks,
  onOpenSpof,
}: {
  tag: PidTag | null;
  onClose: () => void;
  onSelectKks: (kks: SpofKks) => void;
  onOpenSpof: (spofId: string) => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: 0,
        overflow: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Selected equipment
        </Typography>
        {tag && (
          <IconButton size="small" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!tag ? (
        <Typography variant="body2" color="text.secondary">
          Click a KKS on the drawing to see its SPOF record.
        </Typography>
      ) : (
        <>
          <Field label="KKS">
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: RISK_COLORS[4] }}
            >
              {tag.text}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tag.component}
            </Typography>
          </Field>

          <Divider />

          {tag.spofs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No SPOF of the register is linked to this KKS.
            </Typography>
          ) : (
            tag.spofs.map((spof) => (
              <Box
                key={spof.spof_id}
                sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              >
                <Field label="Linked SPOF">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {spof.spof_id}
                  </Typography>
                </Field>
                <Field label="KKS of this SPOF">
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {spof.kks.map((kks) => (
                      <Chip
                        key={kks.kks_id}
                        size="small"
                        label={kks.text ?? kks.kks_id}
                        color={
                          kks.kks_id === tag.kks_id ? "primary" : "default"
                        }
                        variant={
                          kks.kks_id === tag.kks_id ? "filled" : "outlined"
                        }
                        onClick={() => onSelectKks(kks)}
                      />
                    ))}
                  </Box>
                </Field>
                <Field label="Functional location">
                  {spof.functional_location ?? <PendingValue />}
                </Field>
                <Field label="Consequence">
                  {spof.failure_description_short ?? <PendingValue />}
                </Field>
                <Field label="Power loss">
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {spof.power_loss_mw === null ? (
                      <PendingValue />
                    ) : (
                      `${mw(spof.power_loss_mw)} MW`
                    )}
                  </Typography>
                </Field>
                <Field label="Restoration time">
                  {days(spof.restoration_time_days) ?? <PendingValue />}
                </Field>
                <Field label="Current risk">
                  <RiskChip level={spof.risk_category} />
                </Field>
                <Field label="Residual risk">
                  <RiskChip level={spof.residual_risk_category} />
                </Field>
                <Field label="Mitigations">{spof.mitigation_count}</Field>
                <Button
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => onOpenSpof(spof.spof_id)}
                >
                  Open SPOF record
                </Button>
              </Box>
            ))
          )}
        </>
      )}
    </Paper>
  );
}
